<template>
  <div v-editable="blok" class="flex">
    <div class="timeline-steps" ref="timeline">
      <StoryblokComponent
        :style="[stepDistance, stepWidth]"
        class="step-item"
        v-for="section in blok.steps"
        :key="section._uid"
        :blok="section"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SbTimelineStepsStoryblok } from '@/types'
interface Props {
  blok: SbTimelineStepsStoryblok
}
const props = defineProps<Props>()
const timeline = ref<HTMLElement>()
function getPositionAtCenter(element: Element) {
  const { top, left, width, height } = element.getBoundingClientRect()
  return {
    x: left + width / 2,
    y: top + height / 2,
  }
}

function getDistanceBetweenElements(a: Element, b: Element) {
  const aPosition = getPositionAtCenter(a)
  const bPosition = getPositionAtCenter(b)

  return Math.hypot(aPosition.x - bPosition.x, aPosition.y - bPosition.y)
}
const distance = ref()
const stepDistance = computed(() =>
  distance.value
    ? `--step-distance: ${distance.value}px`
    : `step-distance: 200px;`,
)
const setDistance = () => {
  distance.value = getDistanceBetweenElements(
    document?.getElementsByClassName('step-item')[0],
    document?.getElementsByClassName('step-item')[1],
  )
}

const stepWidth = computed(() =>
  props.blok.steps?.length < 6 ? '--step-width: 150px' : '--step-width: 120px',
)
onMounted(() => {
  setDistance()
  window.addEventListener('resize', setDistance)
})
onUnmounted(() => {
  window.removeEventListener('resize', setDistance)
})
</script>

<style lang="scss" scoped>
.flex {
  display: flex;
  justify-content: stretch;
  width: 100%;
}
.timeline-steps {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 30px;

  @media (min-width: $breakpoint-lg) {
    margin: 0;
    flex-wrap: nowrap;
    justify-content: space-between;
  }
}
.step-item {
  --step-width: 120px;
  --step-image-width: 120px;
  --step-distance: 250px;

  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 40%;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  z-index: 0;

  @media (min-width: $breakpoint-lg) {
    width: var(--step-width);
    flex-basis: auto;
  }
}
:deep(.image-wrapper) {
  position: relative;
  z-index: 0;
}
.step-item {
  @media screen and (min-width: $breakpoint-lg) {
    &:not(:last-of-type) {
      & :deep(.image-wrapper) {
        &::after {
          content: '';
          top: 50%;
          left: 50%;
          z-index: -1;
          position: absolute;
          width: var(--step-width);
          height: 1px;
          border-top: 1px dashed #b9b3ac;
        }
      }
    }
  }
  @media screen and (max-width: $breakpoint-lg - 1) {
    &:not(:last-of-type) {
      & :deep(.image-wrapper) {
        &::after {
          content: '';
          top: 50%;
          left: 50%;
          z-index: -1;
          position: absolute;
          width: calc(var(--step-distance) * 1.2);
          height: 1px;
          border-top: 1px dashed #b9b3ac;
        }
      }
    }
    &:nth-of-type(3),
    &:nth-of-type(5) {
      & :deep(.image-wrapper) {
        &::before {
          content: '';
          top: 50%;
          right: 50%;
          z-index: -2;
          position: absolute;
          width: calc(var(--step-distance) * 1.2);
          height: 1px;
          border-top: 1px dashed #b9b3ac;
        }
      }
    }
  }
  @media screen and (max-width: $breakpoint-md - 1) {
    &:not(:last-of-type) {
      & :deep(.image-wrapper) {
        &::after {
          content: '';
          top: 50%;
          left: 50%;
          z-index: -1;
          position: absolute;
          width: calc(var(--step-distance) * 1.2);
          height: 1px;
          border-top: 1px dashed #b9b3ac;
        }
      }
    }
    &:nth-of-type(3),
    &:nth-of-type(5) {
      & :deep(.image-wrapper) {
        &::before {
          content: '';
          top: 50%;
          right: 50%;
          z-index: -2;
          position: absolute;
          width: calc(var(--step-distance));
          height: 1px;
          border-top: 1px dashed #b9b3ac;
        }
      }
    }
  }
}
</style>
